import { skipToken, useQuery } from "@apollo/client/react";
import { Box } from "@mui/material";
// eslint-disable-next-line no-restricted-imports
import { InlineError } from "@pmag/ui-components";
import { PriceInfoContainer } from "@src/components/priceInfoContainer/PriceInfoContainer";
import { type GQLComponentGroupOverviewModelQuery, type GQLComponentGroupOverviewModelQueryVariables } from "@src/graphql.generated";
import { useAttributeConfig } from "@src/hooks/useAttributeConfig";
import { useGlobalState } from "@src/hooks/useGlobalState";
import { componentGroupOverviewModelQuery } from "@src/pages/componentGroupOverview/ComponentGroupOverviewPage.gql";
import { ArticleHeader } from "@src/pages/componentGroupOverview/components/articleHeader/ArticleHeader";
import { getSelectedComponentGroup } from "@src/pages/subGroupDetail/SubgroupDetailPage.utils";
import { SubgroupDetailPageSkeleton } from "@src/pages/subGroupDetail/SubgroupDetailPageSkeleton";
import { SubgroupDetailsListSection } from "@src/pages/subGroupDetail/subgroupDetailsListSection/SubgroupDetailsListSection";
import { dispatchRequestWishlistInfoEvent, Events } from "@src/utils/events";
import { isSPFWishlistInfoEvent } from "@src/utils/typeGuard.utils";
import { type FunctionComponent, useEffect, useMemo, useState } from "react";

export const dataTestId = {
    pageContainer: "sparePartsFinder.subgroupDetailPage.container",
    footer: "sparePartsFinder.subgroupDetailPage.button.footer",
    selectedCount: "sparePartsFinder.subgroupDetailPage.button.footer.selectedCount",
    loadingSkeleton: "sparePartsFinder.subgroupDetailPage.loadingSkeleton",
    inlineError: "sparePartsFinder.subgroupDetailPage.inlineError",
    createClaimButton: "sparePartsFinder.subgroupDetailPage.createClaimButton",
    addToCartButton: "sparePartsFinder.subgroupDetailPage.addToCartButton",
    addToClaimButton: "sparePartsFinder.subgroupDetailPage.addToClaimButton",
    resetArticlesButton: "sparePartsFinder.subgroupDetailPage.resetArticlesButton",
};

export const SubGroupDetailPage: FunctionComponent = () => {
    const { modelId, componentGroupId } = useGlobalState();
    const { wishlistEnabled, wishlistClientside } = useAttributeConfig();
    const [clientWishlist, setClientWishlist] = useState<string[]>([]);

    const { data, error, loading, refetch } = useQuery<GQLComponentGroupOverviewModelQuery, GQLComponentGroupOverviewModelQueryVariables>(
        componentGroupOverviewModelQuery,
        modelId ? { variables: { id: modelId } } : skipToken,
    );

    useEffect(() => {
        if (wishlistEnabled && wishlistClientside) {
            dispatchRequestWishlistInfoEvent();
        }
    }, [wishlistEnabled, wishlistClientside]);

    useEffect(() => {
        if (wishlistEnabled && wishlistClientside) {
            const handleWishlistInfoEvent = (event: CustomEvent) => {
                if (isSPFWishlistInfoEvent(event)) {
                    setClientWishlist(event.detail.wishlistArticleNumbers);
                }
            };

            document.addEventListener(Events.SPFWishlistInfo, handleWishlistInfoEvent);

            return () => document.removeEventListener(Events.SPFWishlistInfo, handleWishlistInfoEvent);
        }
    }, [wishlistEnabled, wishlistClientside]);

    const model = data?.model;

    const selectedComponentGroup = useMemo(() => {
        const frameComponents = model ? model.frameComponentGroupParents : [];
        const engineComponents = model ? model.engineComponentGroupParents : [];

        return getSelectedComponentGroup([...frameComponents, ...engineComponents], componentGroupId);
    }, [componentGroupId, model]);

    if (loading) return <SubgroupDetailPageSkeleton data-testid={dataTestId.loadingSkeleton} />;

    if (error && !model) return <InlineError onRetry={refetch} data-testid={dataTestId.inlineError} />;

    return (
        <Box data-testid={dataTestId.pageContainer}>
            <ArticleHeader model={model} />

            {model?.articleNumber && (
                <SubgroupDetailsListSection
                    brand={model.brand ? model.brand.shortName : ""}
                    selectedComponentGroup={selectedComponentGroup}
                    frameComponents={model.frameComponentGroupParents}
                    engineComponents={model.engineComponentGroupParents}
                    modelId={model.id}
                    modelArticleNumber={model.articleNumber}
                    clientWishlist={clientWishlist}
                />
            )}

            <PriceInfoContainer />
        </Box>
    );
};
